import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useVaults } from '../hooks/useVaults';

interface TrustedAgent {
  id: string;
  name: string;
  type: 'ericspace' | 'claude_mcp' | 'cursor' | 'antigravity' | 'custom';
  key_prefix: string;
  allowed_vaults: string;
  vault_names?: string[];
  permissions: 'read_only' | 'read_write';
  monthly_quota: number;
  used_this_month: number;
  status: 'active' | 'suspended';
  created_at: string;
  expires_at: string;
}

export function TrustedAgentsPage() {
  const { vaults } = useVaults();
  const [agents, setAgents] = useState<TrustedAgent[]>([]);
  const [loading, setLoading] = useState(true);

  // Tabs
  const [activeTab, setActiveTab] = useState<'mcp' | 'rest' | 'sandbox' | 'tokens'>('mcp');
  const [clientTab, setClientTab] = useState<'cursor' | 'claude' | 'n8n'>('cursor');
  const [sdkTab, setSdkTab] = useState<'python' | 'nodejs' | 'curl'>('python');

  // Sandbox Tester State
  const [sandboxTarget, setSandboxTarget] = useState<'chat' | 'models' | 'mcp_sse'>('chat');
  const [sandboxPrompt, setSandboxPrompt] = useState('Xin chào Erix One, hãy giới thiệu năng lực tự động hóa tài liệu và tích hợp OnlyOffice của bạn.');
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [sandboxResult, setSandboxResult] = useState<any>(null);
  const [sandboxLatency, setSandboxLatency] = useState<number | null>(null);
  const [sandboxStatus, setSandboxStatus] = useState<number | null>(null);

  // Copy notification
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState<TrustedAgent | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [form, setForm] = useState({
    name: '',
    type: 'ericspace' as TrustedAgent['type'],
    allowed_vaults: 'all',
    selectedVaultIds: [] as string[],
    permissions: 'read_only' as TrustedAgent['permissions'],
    monthly_quota: 20000,
    expires_at: '2027-12-31'
  });

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    setLoading(true);
    try {
      const res = await api.getTrustedAgents();
      setAgents(res.agents || []);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!form.name.trim()) {
      alert('Vui lòng nhập tên nhận diện cho Agent!');
      return;
    }
    setSubmitting(true);
    try {
      const vaultNames = form.allowed_vaults === 'all' 
        ? ['Tất cả Kho báu']
        : vaults.filter(v => form.selectedVaultIds.includes(v.id)).map(v => v.name);

      const res = await api.createTrustedAgent({
        name: form.name.trim(),
        type: form.type,
        allowed_vaults: form.allowed_vaults,
        vault_names: vaultNames,
        permissions: form.permissions,
        monthly_quota: Number(form.monthly_quota) || 10000,
        expires_at: form.expires_at
      });

      if (res.agent) {
        setAgents([res.agent, ...agents]);
        setCreatedSecret(res.token);
      }
      setShowCreateModal(false);
      setActiveTab('tokens');
      setForm({
        name: '',
        type: 'ericspace',
        allowed_vaults: 'all',
        selectedVaultIds: [],
        permissions: 'read_only',
        monthly_quota: 20000,
        expires_at: '2027-12-31'
      });
    } catch (e: any) {
      alert('Lỗi tạo token: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeAgent = async (agentId: string, name: string) => {
    if (!confirm(`Bạn có chắc chắn muốn thu hồi quyền truy cập của Agent "${name}"? Thao tác này sẽ vô hiệu hóa token ngay lập tức.`)) {
      return;
    }
    try {
      await api.deleteTrustedAgent(agentId);
      setAgents(agents.filter(a => a.id !== agentId));
    } catch (e: any) {
      alert('Lỗi thu hồi token: ' + e.message);
    }
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Run Sandbox Test
  const runSandboxTest = async () => {
    setSandboxLoading(true);
    setSandboxResult(null);
    setSandboxLatency(null);
    setSandboxStatus(null);
    const start = Date.now();

    try {
      if (sandboxTarget === 'models') {
        const res = await api.getModels();
        setSandboxLatency(Date.now() - start);
        setSandboxStatus(200);
        setSandboxResult(res);
      } else if (sandboxTarget === 'mcp_sse') {
        const res = await api.testMcpHandshake();
        setSandboxLatency(Date.now() - start);
        setSandboxStatus(res.status);
        setSandboxResult(res);
      } else {
        const res = await api.testChatCompletion(sandboxPrompt, 'erix-one');
        setSandboxLatency(Date.now() - start);
        setSandboxStatus(200);
        setSandboxResult(res);
      }
    } catch (err: any) {
      setSandboxLatency(Date.now() - start);
      setSandboxStatus(500);
      setSandboxResult({ error: err.message || 'Kiểm thử thất bại' });
    } finally {
      setSandboxLoading(false);
    }
  };

  const domain = 'one.wowerix.com';
  const sampleToken = agents.length > 0 ? `${agents[0].key_prefix.replace('...', '')}abc123xyz` : 'erix_commercial_key_demo';

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-muted)' }}>
        Đang tải cấu hình kết nối API & MCP...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '14px', overflowY: 'auto', paddingRight: '4px' }}>

      {/* Header */}
      <div className="page-header" style={{ flexShrink: 0, paddingBottom: '4px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 className="page-title">🔌 Cổng Thương Mại API & MCP Server</h1>
            <span className="badge badge--purple" style={{ fontSize: '11px', padding: '3px 10px' }}>
              PaaM & MCP Spec v1.0
            </span>
          </div>
          <p className="page-subtitle">
            Chuẩn hóa Platform-as-a-Model (<code style={{ color: 'var(--accent-cyan)' }}>erix-one</code>) và MCP Server (<code style={{ color: 'var(--accent-cyan)' }}>/mcp/sse</code>) phục vụ Cursor, Claude Desktop, n8n và các đối tác B2B
          </p>
        </div>

        <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
          ➕ Cấp Token Mới
        </button>
      </div>

      {/* Secret Banner */}
      {createdSecret && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.25) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.5)',
            borderRadius: '10px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🔑</span>
            <div>
              <div style={{ fontWeight: 600, color: '#34d399', fontSize: '13px' }}>
                ĐÃ KHỞI TẠO TOKEN THƯƠNG MẠI MỚI THÀNH CÔNG!
              </div>
              <div style={{ fontSize: '11.5px', color: '#e2e8f0', marginTop: '2px' }}>
                Chuỗi token này chỉ hiển thị <strong style={{ color: '#fbbf24' }}>DUY NHẤT MỘT LẦN</strong>. Hãy sao chép và lưu vào nơi an toàn:
              </div>
              <div style={{ marginTop: '6px', fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#a7f3d0', background: 'rgba(0,0,0,0.4)', padding: '4px 10px', borderRadius: '6px', wordBreak: 'break-all' }}>
                {createdSecret}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => copyToClipboard(createdSecret, 'created_token')}
              style={{ background: copiedKey === 'created_token' ? 'rgba(16,185,129,0.3)' : undefined }}
            >
              {copiedKey === 'created_token' ? '✅ Đã Copy' : '📋 Sao Chép Token'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => setCreatedSecret(null)}>
              ✕ Đóng
            </button>
          </div>
        </div>
      )}

      {/* Main Tab Navigation */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '10px', flexShrink: 0 }}>
        {[
          { key: 'mcp' as const, label: '🔌 Cổng MCP Server (/mcp/sse)', badge: 'Cursor & Claude' },
          { key: 'rest' as const, label: '🤖 Cổng REST API (/v1)', badge: 'OpenAI Spec' },
          { key: 'sandbox' as const, label: '⚡ Interactive Sandbox Tester', badge: 'Live Probe' },
          { key: 'tokens' as const, label: `🔑 Quản Lý Scoped Tokens (${agents.length})`, badge: 'Security' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: activeTab === tab.key ? '1px solid var(--accent-primary)' : '1px solid transparent',
              background: activeTab === tab.key ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255,255,255,0.03)',
              color: activeTab === tab.key ? '#f1f5f9' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500,
              fontSize: '13px',
              transition: 'all 0.2s'
            }}
          >
            <span>{tab.label}</span>
            <span style={{
              fontSize: '10px',
              padding: '1px 6px',
              borderRadius: '10px',
              background: activeTab === tab.key ? 'var(--accent-primary)' : 'rgba(255,255,255,0.08)',
              color: '#ffffff'
            }}>
              {tab.badge}
            </span>
          </button>
        ))}
      </div>

      {/* TAB 1: PLATFORM-AS-AN-MCP-SERVER */}
      {activeTab === 'mcp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Handshake Endpoints Card */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>📡</span>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#f8fafc' }}>Thông Số Kết Nối Giao Thức Model Context Protocol (MCP)</h3>
              </div>
              <span className="badge badge--green" style={{ fontSize: '11px' }}>SSE Protocol: 2024-11-05</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>SSE Handshake Endpoint (GET):</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <code style={{ color: 'var(--accent-cyan)', fontSize: '12.5px' }}>https://{domain}/mcp/sse</code>
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => copyToClipboard(`https://${domain}/mcp/sse`, 'sse_url')}
                  >
                    {copiedKey === 'sse_url' ? '✅' : '📋 Copy'}
                  </button>
                </div>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>JSON-RPC Message Endpoint (POST):</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <code style={{ color: 'var(--accent-cyan)', fontSize: '12.5px' }}>https://{domain}/mcp/messages</code>
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => copyToClipboard(`https://${domain}/mcp/messages`, 'msg_url')}
                  >
                    {copiedKey === 'msg_url' ? '✅' : '📋 Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Client Setup Guides */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#f8fafc' }}>⚙️ Cấu Hình Kết Nối Dành Cho Khách Hàng Bên Ngoài</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { key: 'cursor' as const, label: '⚡ Cursor IDE' },
                  { key: 'claude' as const, label: '🤖 Claude Desktop' },
                  { key: 'n8n' as const, label: '🔄 n8n Automation' }
                ].map(c => (
                  <button
                    key={c.key}
                    onClick={() => setClientTab(c.key)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      background: clientTab === c.key ? 'var(--accent-primary)' : 'rgba(255,255,255,0.05)',
                      color: clientTab === c.key ? '#ffffff' : 'var(--text-secondary)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {clientTab === 'cursor' && (
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Mở <strong>Cursor Settings → Features → MCP Servers → Add New MCP Server</strong> hoặc thêm vào tệp cấu hình:
                </p>
                <div style={{ position: 'relative' }}>
                  <pre style={{ margin: 0, padding: '14px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '12px', overflowX: 'auto' }}>
{`{
  "mcpServers": {
    "erix-one": {
      "url": "https://${domain}/mcp/sse",
      "headers": {
        "Authorization": "Bearer ${sampleToken}"
      }
    }
  }
}`}
                  </pre>
                  <button
                    className="btn btn--secondary btn--sm"
                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                    onClick={() => copyToClipboard(`{\n  "mcpServers": {\n    "erix-one": {\n      "url": "https://${domain}/mcp/sse",\n      "headers": {\n        "Authorization": "Bearer ${sampleToken}"\n      }\n    }\n  }\n}`, 'cursor_cfg')}
                  >
                    {copiedKey === 'cursor_cfg' ? '✅ Đã Copy' : '📋 Copy JSON'}
                  </button>
                </div>
              </div>
            )}

            {clientTab === 'claude' && (
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Dán vào tệp cấu hình Claude Desktop: <code>%APPDATA%\\Claude\\claude_desktop_config.json</code>:
                </p>
                <div style={{ position: 'relative' }}>
                  <pre style={{ margin: 0, padding: '14px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '12px', overflowX: 'auto' }}>
{`{
  "mcpServers": {
    "erix-one": {
      "command": "npx",
      "args": [
        "-y",
        "mcp-proxy",
        "https://${domain}/mcp/sse",
        "--header",
        "Authorization: Bearer ${sampleToken}"
      ]
    }
  }
}`}
                  </pre>
                  <button
                    className="btn btn--secondary btn--sm"
                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                    onClick={() => copyToClipboard(`{\n  "mcpServers": {\n    "erix-one": {\n      "command": "npx",\n      "args": [\n        "-y",\n        "mcp-proxy",\n        "https://${domain}/mcp/sse",\n        "--header",\n        "Authorization: Bearer ${sampleToken}"\n      ]\n    }\n  }\n}`, 'claude_cfg')}
                  >
                    {copiedKey === 'claude_cfg' ? '✅ Đã Copy' : '📋 Copy JSON'}
                  </button>
                </div>
              </div>
            )}

            {clientTab === 'n8n' && (
              <div>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Thêm node <strong>MCP Client Tool</strong> trong quy trình tự động hóa n8n:
                </p>
                <div style={{ background: 'rgba(0,0,0,0.4)', padding: '12px', borderRadius: '8px', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div>• <strong>Transport Type:</strong> <code>Server-Sent Events (SSE)</code></div>
                  <div>• <strong>Endpoint URL:</strong> <code>https://{domain}/mcp/sse</code></div>
                  <div>• <strong>Header:</strong> <code>Authorization: Bearer {sampleToken}</code></div>
                  <div style={{ color: '#34d399', marginTop: '4px' }}>
                    ✓ Khi lưu node, n8n sẽ tự động tải danh sách 4 công cụ của Erix One vào workflow.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tool Manifest Catalog (§3.2.E) */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#f8fafc' }}>
              📦 Danh Mục 4 Công Cụ Nghiệp Vụ Chuẩn Hóa (Erix One MCP Tools)
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                {
                  name: 'erix_one_document_automate',
                  badge: 'Core OnlyOffice & DIaaS',
                  desc: 'Số hóa, biên tập và tự động hóa tác vụ văn phòng (hợp đồng, báo cáo, biểu mẫu) tích hợp OnlyOffice.',
                  schema: 'document_id: string, action: "summarize" | "extract_contract_terms" | "export_pdf" | "generate_spreadsheet"'
                },
                {
                  name: 'erix_one_search_documents',
                  badge: 'Semantic & Full-text',
                  desc: 'Tìm kiếm tài liệu thông minh trong Kho tri thức Erix One bằng từ khóa hoặc ngữ nghĩa vector.',
                  schema: 'query: string, limit?: number (mặc định 5)'
                },
                {
                  name: 'erix_one_read_document',
                  badge: 'Markdown Reader',
                  desc: 'Đọc toàn bộ nội dung văn bản Markdown hoặc bảng biểu đã được số hóa của tài liệu theo document_id.',
                  schema: 'document_id: string'
                },
                {
                  name: 'erix_one_query_vault',
                  badge: 'RAG Knowledge Synthesis',
                  desc: 'Hỏi đáp thông minh RAG tổng hợp kiến thức từ các tài liệu trong Kho tri thức Erix One.',
                  schema: 'question: string, doc_ids?: string[]'
                }
              ].map((tool, idx) => (
                <div key={idx} style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <code style={{ color: '#38bdf8', fontWeight: 600, fontSize: '13px' }}>{tool.name}</code>
                    <span className="badge badge--purple" style={{ fontSize: '10px' }}>{tool.badge}</span>
                  </div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    {tool.desc}
                  </p>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    Params: {tool.schema}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* TAB 2: PLATFORM-AS-A-MODEL (REST API /v1) */}
      {activeTab === 'rest' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Ingress Specs */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>🤖</span>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#f8fafc' }}>Thông Số Cổng REST API: Platform-as-a-Model (PaaM)</h3>
              </div>
              <span className="badge badge--blue" style={{ fontSize: '11px' }}>OpenAI Compatible</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Base Ingress URL:</div>
                <code style={{ color: 'var(--accent-cyan)', fontSize: '13px' }}>https://{domain}/v1</code>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Model ID Thương Hiệu:</div>
                <code style={{ color: '#38bdf8', fontSize: '13px', fontWeight: 600 }}>erix-one</code>
              </div>

              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>Streaming Capability:</div>
                <span style={{ color: '#34d399', fontSize: '12px', fontWeight: 600 }}>SSE Zero-Copy (TTFT &lt; 200ms)</span>
              </div>
            </div>
          </div>

          {/* Endpoints Table */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#f8fafc' }}>Endpoints Chuẩn OpenAI Được Hỗ Trợ</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px' }}>
                <span className="badge badge--green" style={{ fontSize: '11px', width: '55px', textAlign: 'center' }}>GET</span>
                <code style={{ color: '#f1f5f9', fontSize: '13px' }}>/v1/models</code>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', marginLeft: 'auto' }}>
                  Khám phá model danh mục cho n8n & Cursor dropdown menu
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(0,0,0,0.3)', padding: '10px 14px', borderRadius: '8px' }}>
                <span className="badge badge--blue" style={{ fontSize: '11px', width: '55px', textAlign: 'center' }}>POST</span>
                <code style={{ color: '#f1f5f9', fontSize: '13px' }}>/v1/chat/completions</code>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px', marginLeft: 'auto' }}>
                  Giao tiếp hội thoại thông minh (hỗ trợ cả stream: true và stream: false)
                </span>
              </div>
            </div>
          </div>

          {/* Code SDK Examples */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#f8fafc' }}>💻 Mã Nguồn Kết Nối Chuẩn SDK (0 Cần Sửa Thư Viện)</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { key: 'python' as const, label: '🐍 Python (OpenAI)' },
                  { key: 'nodejs' as const, label: '📦 Node.js (OpenAI)' },
                  { key: 'curl' as const, label: '⚡ cURL' }
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => setSdkTab(s.key)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      background: sdkTab === s.key ? 'var(--accent-primary)' : 'rgba(255,255,255,0.05)',
                      color: sdkTab === s.key ? '#ffffff' : 'var(--text-secondary)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {sdkTab === 'python' && (
              <div style={{ position: 'relative' }}>
                <pre style={{ margin: 0, padding: '14px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '12px', overflowX: 'auto' }}>
{`from openai import OpenAI

client = OpenAI(
    base_url="https://${domain}/v1",
    api_key="${sampleToken}"
)

# Gửi yêu cầu phân tích tài liệu và tự động hóa
response = client.chat.completions.create(
    model="erix-one",
    messages=[
        {"role": "user", "content": "Tóm tắt và bóc tách các điều khoản chính của hợp đồng mẫu"}
    ],
    stream=True
)

for chunk in response:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="", flush=True)`}
                </pre>
                <button
                  className="btn btn--secondary btn--sm"
                  style={{ position: 'absolute', top: '8px', right: '8px' }}
                  onClick={() => copyToClipboard(`from openai import OpenAI\n\nclient = OpenAI(\n    base_url="https://${domain}/v1",\n    api_key="${sampleToken}"\n)\n\nresponse = client.chat.completions.create(\n    model="erix-one",\n    messages=[\n        {"role": "user", "content": "Tóm tắt và bóc tách các điều khoản chính của hợp đồng mẫu"}\n    ],\n    stream=True\n)\n\nfor chunk in response:\n    if chunk.choices[0].delta.content:\n        print(chunk.choices[0].delta.content, end="", flush=True)`, 'py_code')}
                >
                  {copiedKey === 'py_code' ? '✅ Đã Copy' : '📋 Copy Python'}
                </button>
              </div>
            )}

            {sdkTab === 'nodejs' && (
              <div style={{ position: 'relative' }}>
                <pre style={{ margin: 0, padding: '14px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '12px', overflowX: 'auto' }}>
{`import OpenAI from 'openai';

const openai = new OpenAI({
  baseURL: 'https://${domain}/v1',
  apiKey: '${sampleToken}'
});

async function main() {
  const completion = await openai.chat.completions.create({
    model: 'erix-one',
    messages: [
      { role: 'user', content: 'Hướng dẫn tự động hóa báo cáo tài chính bằng OnlyOffice' }
    ]
  });

  console.log(completion.choices[0].message.content);
}

main();`}
                </pre>
                <button
                  className="btn btn--secondary btn--sm"
                  style={{ position: 'absolute', top: '8px', right: '8px' }}
                  onClick={() => copyToClipboard(`import OpenAI from 'openai';\n\nconst openai = new OpenAI({\n  baseURL: 'https://${domain}/v1',\n  apiKey: '${sampleToken}'\n});\n\nasync function main() {\n  const completion = await openai.chat.completions.create({\n    model: 'erix-one',\n    messages: [\n      { role: 'user', content: 'Hướng dẫn tự động hóa báo cáo tài chính bằng OnlyOffice' }\n    ]\n  });\n\n  console.log(completion.choices[0].message.content);\n}\n\nmain();`, 'node_code')}
                >
                  {copiedKey === 'node_code' ? '✅ Đã Copy' : '📋 Copy Node.js'}
                </button>
              </div>
            )}

            {sdkTab === 'curl' && (
              <div style={{ position: 'relative' }}>
                <pre style={{ margin: 0, padding: '14px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '12px', overflowX: 'auto' }}>
{`curl -X POST https://${domain}/v1/chat/completions \\
  -H "Authorization: Bearer ${sampleToken}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "erix-one",
    "messages": [
      { "role": "user", "content": "Xin chào Cụm Swarm Erix One" }
    ],
    "stream": false
  }'`}
                </pre>
                <button
                  className="btn btn--secondary btn--sm"
                  style={{ position: 'absolute', top: '8px', right: '8px' }}
                  onClick={() => copyToClipboard(`curl -X POST https://${domain}/v1/chat/completions \\\n  -H "Authorization: Bearer ${sampleToken}" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "model": "erix-one",\n    "messages": [\n      { "role": "user", "content": "Xin chào Cụm Swarm Erix One" }\n    ],\n    "stream": false\n  }'`, 'curl_code')}
                >
                  {copiedKey === 'curl_code' ? '✅ Đã Copy' : '📋 Copy cURL'}
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* TAB 3: INTERACTIVE SANDBOX TESTER */}
      {activeTab === 'sandbox' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#f8fafc' }}>
              ⚡ Kiểm Thử Trực Quan Thời Gian Thực (Live Probe Console)
            </h3>

            {/* Target selector */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {[
                { key: 'chat' as const, label: 'POST /v1/chat/completions (Model erix-one)' },
                { key: 'models' as const, label: 'GET /v1/models (Discovery)' },
                { key: 'mcp_sse' as const, label: 'GET /mcp/sse (Handshake)' }
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setSandboxTarget(t.key)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    background: sandboxTarget === t.key ? 'var(--accent-primary)' : 'rgba(255,255,255,0.05)',
                    color: sandboxTarget === t.key ? '#ffffff' : 'var(--text-secondary)',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Prompt input if chat target */}
            {sandboxTarget === 'chat' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>
                  Nội dung câu hỏi (User Prompt):
                </label>
                <textarea
                  className="input"
                  rows={3}
                  value={sandboxPrompt}
                  onChange={e => setSandboxPrompt(e.target.value)}
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>
            )}

            <button
              className="btn btn--primary"
              onClick={runSandboxTest}
              disabled={sandboxLoading}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {sandboxLoading ? '⏳ Đang gửi Probe...' : '⚡ Bắt Đầu Kiểm Thử'}
            </button>
          </div>

          {/* Test Results Window */}
          {(sandboxResult !== null || sandboxLoading) && (
            <div style={{ background: '#0a0d14', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>Kết Quả Phản Hồi:</span>
                  {sandboxStatus && (
                    <span className={`badge badge--${sandboxStatus >= 200 && sandboxStatus < 300 ? 'green' : 'red'}`} style={{ fontSize: '11px' }}>
                      HTTP {sandboxStatus}
                    </span>
                  )}
                  {sandboxLatency !== null && (
                    <span className="badge badge--purple" style={{ fontSize: '11px' }}>
                      ⏱️ {sandboxLatency} ms
                    </span>
                  )}
                  <span className="badge badge--cyan" style={{ fontSize: '11px' }}>
                    Engine: Erix-One-Swarm-v1.0
                  </span>
                </div>

                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => copyToClipboard(JSON.stringify(sandboxResult, null, 2), 'sandbox_res')}
                >
                  {copiedKey === 'sandbox_res' ? '✅ Đã Copy' : '📋 Copy Kết Quả'}
                </button>
              </div>

              {sandboxLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                  Đang thiết lập kết nối tới Cụm Swarm Erix One...
                </div>
              ) : (
                <pre style={{ margin: 0, padding: '12px', background: 'rgba(0,0,0,0.6)', borderRadius: '8px', fontSize: '12px', color: '#38bdf8', overflowX: 'auto', maxHeight: '350px' }}>
                  {JSON.stringify(sandboxResult, null, 2)}
                </pre>
              )}
            </div>
          )}

        </div>
      )}

      {/* TAB 4: QUẢN LÝ SCOPED TOKENS & ỦY QUYỀN */}
      {activeTab === 'tokens' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Preset Connectors Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', flexShrink: 0 }}>
            {[
              {
                type: 'ericspace' as const,
                title: '🖥️ EricSpace Desktop',
                desc: 'Đồng bộ 2 chiều với ứng dụng EricSpace và OnlyOffice trên máy tính cục bộ (127.0.0.1:8765).'
              },
              {
                type: 'claude_mcp' as const,
                title: '🤖 Claude Desktop MCP',
                desc: 'Giao tiếp tiêu chuẩn Model Context Protocol (MCP) cho phép Claude tra cứu tài liệu ErixOne.'
              },
              {
                type: 'cursor' as const,
                title: '⚡ Cursor / VS Code',
                desc: 'Cung cấp ngữ cảnh tài liệu kiến trúc, đặc tả API và SOP cho kỹ sư lập trình trong IDE.'
              },
              {
                type: 'antigravity' as const,
                title: '🌌 Antigravity Agent',
                desc: 'Tự động hóa tác vụ chuyên sâu với Deepmind Antigravity CLI và subagent orchestration.'
              }
            ].map((item, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: '10px'
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#f8fafc' }}>{item.title}</h3>
                  <p style={{ margin: 0, fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{item.desc}</p>
                </div>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => {
                    setForm(prev => ({ ...prev, type: item.type, name: `${item.title.split(' ')[1]} Connector` }));
                    setShowCreateModal(true);
                  }}
                >
                  Cấp Token Ngay →
                </button>
              </div>
            ))}
          </div>

          {/* Active Delegated Agents Table */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#f1f5f9' }}>
              Danh Sách AI Agents Đang Được Cấp Khóa ({agents.length})
            </h2>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tên Agent & Phân Loại</th>
                    <th>Mã Khóa (Prefix)</th>
                    <th>Phạm Vi Kho Báu Được Phép</th>
                    <th>Quyền Hạn</th>
                    <th>Hạn Mức Tháng</th>
                    <th>Hết Hạn</th>
                    <th>Trạng Thái</th>
                    <th style={{ width: '160px' }}>Thao Tác</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                        Chưa có AI Agent nào được cấp quyền ủy thác. Hãy bấm nút "➕ Cấp Quyền Cho Agent Mới" để bắt đầu.
                      </td>
                    </tr>
                  ) : (
                    agents.map(ag => (
                      <tr key={ag.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '16px' }}>
                              {ag.type === 'ericspace' ? '🖥️' : ag.type === 'claude_mcp' ? '🤖' : ag.type === 'cursor' ? '⚡' : ag.type === 'antigravity' ? '🌌' : '🔌'}
                            </span>
                            <div>
                              <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '13px' }}>{ag.name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ag.type.toUpperCase()}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <code style={{ fontSize: '11.5px', color: 'var(--accent-cyan)' }}>{ag.key_prefix}</code>
                        </td>
                        <td>
                          <span className="badge badge--gray" style={{ fontSize: '11px' }}>
                            {ag.allowed_vaults === 'all' ? '🌐 Toàn bộ Kho báu' : `${ag.vault_names?.length || 1} Kho báu chỉ định`}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge--${ag.permissions === 'read_write' ? 'purple' : 'blue'}`} style={{ fontSize: '11px' }}>
                            {ag.permissions === 'read_write' ? 'Đọc & Ghi' : 'Chỉ Đọc'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '12px' }}>
                            {ag.used_this_month.toLocaleString()} / <strong>{ag.monthly_quota.toLocaleString()}</strong>
                          </div>
                          <div style={{ width: '80px', height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, (ag.used_this_month / ag.monthly_quota) * 100)}%`, height: '100%', background: 'var(--accent-primary)' }} />
                          </div>
                        </td>
                        <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{ag.expires_at}</td>
                        <td>
                          <span className="badge badge--green" style={{ fontSize: '11px' }}>Hoạt động</span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              className="btn btn--secondary btn--sm"
                              onClick={() => setShowConfigModal(ag)}
                              title="Xem cấu hình kết nối JSON"
                            >
                              📋 Config
                            </button>
                            <button
                              className="btn btn--danger btn--sm"
                              onClick={() => handleRevokeAgent(ag.id, ag.name)}
                              title="Thu hồi quyền truy cập"
                            >
                              🚫
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* MODAL: Config Modal */}
      {showConfigModal && (
        <div className="modal-backdrop" onClick={() => setShowConfigModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal__header">
              <h2 className="modal__title">📋 Cấu Hình Cho {showConfigModal.name}</h2>
              <button className="btn btn--ghost btn--sm" onClick={() => setShowConfigModal(null)}>✕</button>
            </div>
            <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                Sử dụng đoạn cấu hình này để nhúng vào công cụ bên ngoài:
              </p>

              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>
                  Cấu hình MCP Settings (Claude / Cursor / n8n):
                </label>
                <div style={{ position: 'relative' }}>
                  <pre style={{ margin: 0, padding: '12px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px', fontSize: '11.5px', overflowX: 'auto' }}>
{`{
  "mcpServers": {
    "erix-one": {
      "url": "https://${domain}/mcp/sse",
      "headers": {
        "Authorization": "Bearer ${showConfigModal.key_prefix}"
      }
    }
  }
}`}
                  </pre>
                  <button
                    className="btn btn--secondary btn--sm"
                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                    onClick={() => copyToClipboard(`{\n  "mcpServers": {\n    "erix-one": {\n      "url": "https://${domain}/mcp/sse",\n      "headers": {\n        "Authorization": "Bearer ${showConfigModal.key_prefix}"\n      }\n    }\n  }\n}`, 'modal_cfg')}
                  >
                    {copiedKey === 'modal_cfg' ? '✅ Đã Copy' : '📋 Copy'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn--primary" onClick={() => setShowConfigModal(null)}>Đã hiểu</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Create New Agent Modal */}
      {showCreateModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal__header">
              <h2 className="modal__title">➕ Cấp Quyền Cho Agent / Khách Hàng B2B</h2>
              <button className="btn btn--ghost btn--sm" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Tên Định Danh Token *</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ví dụ: Claude Desktop Assistant, n8n Contract Workflow..."
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Phân Loại Agent</label>
                  <select
                    className="input"
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value as any })}
                  >
                    <option value="ericspace">🖥️ EricSpace Desktop Bridge</option>
                    <option value="claude_mcp">🤖 Claude Desktop MCP</option>
                    <option value="cursor">⚡ Cursor / VS Code</option>
                    <option value="antigravity">🌌 Deepmind Antigravity</option>
                    <option value="custom">🔌 Custom B2B Client / n8n</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Quyền Hạn</label>
                  <select
                    className="input"
                    value={form.permissions}
                    onChange={e => setForm({ ...form, permissions: e.target.value as any })}
                  >
                    <option value="read_only">Chỉ Đọc (Read-only)</option>
                    <option value="read_write">Đọc & Ghi (Read & Write)</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Phạm Vi Kho Báu Được Phép Truy Cập</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="allowed_vaults"
                      checked={form.allowed_vaults === 'all'}
                      onChange={() => setForm({ ...form, allowed_vaults: 'all' })}
                    />
                    Tất cả Kho báu
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="allowed_vaults"
                      checked={form.allowed_vaults === 'custom'}
                      onChange={() => setForm({ ...form, allowed_vaults: 'custom' })}
                    />
                    Chỉ định Kho báu
                  </label>
                </div>
              </div>

              {form.allowed_vaults === 'custom' && (
                <div style={{ background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>Chọn các Kho báu cho phép:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                    {vaults.map(v => {
                      const isSelected = form.selectedVaultIds.includes(v.id);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => {
                            setForm({
                              ...form,
                              selectedVaultIds: isSelected
                                ? form.selectedVaultIds.filter(id => id !== v.id)
                                : [...form.selectedVaultIds, v.id]
                            });
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(124, 58, 237, 0.25)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)'}`,
                            color: isSelected ? '#d8b4fe' : 'var(--text-muted)'
                          }}
                        >
                          {isSelected ? '✅ ' : '➕ '}{v.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Hạn Mức Requests/Tháng</label>
                  <input
                    type="number"
                    className="input"
                    value={form.monthly_quota}
                    onChange={e => setForm({ ...form, monthly_quota: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Ngày Hết Hạn</label>
                  <input
                    type="date"
                    className="input"
                    value={form.expires_at}
                    onChange={e => setForm({ ...form, expires_at: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn--secondary" onClick={() => setShowCreateModal(false)}>Hủy</button>
              <button className="btn btn--primary" onClick={handleCreateAgent} disabled={submitting}>
                {submitting ? 'Đang tạo Token...' : 'Tạo Token Ủy Thác'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
